import type { FormAnswers, QuestionForm } from './forms.ts';

// Fachliche Entitäten, wie sie zwischen Server und Clients synchronisiert werden.
// Zeitstempel sind ISO-Strings (UTC). IDs sind clientseitig erzeugte UUIDv7.

export type Role = 'owner' | 'admin' | 'mitglied';

export type TaskStatus = 'offen' | 'in_arbeit' | 'zur_pruefung' | 'erledigt';

export type Priority = 'niedrig' | 'normal' | 'hoch';

export type ProjectView = 'liste' | 'board';

export type MessageKind =
  | 'text'
  | 'bild'
  | 'video'
  | 'sprache'
  | 'datei'
  | 'link'
  | 'meilenstein'
  | 'rueckfrage'
  | 'system';

/** Rolle einer Nachricht im Schritt-Ablauf Problem → Lösung → Prüfung. */
export type StepRole = 'problem' | 'loesung' | 'pruefung';

export type CheckResult = 'ok' | 'nicht_ok';

export type AuthorKind = 'user' | 'guest' | 'system';

export type MediaStatus = 'hochladen' | 'verarbeiten' | 'fertig' | 'fehler';

/**
 * Geschäft (Geschäftsbereich) eines Tenants. Normalfall: genau eins.
 * KI-Keys, Modelle und Prompts gelten je Geschäft, damit Verbrauch und
 * Abrechnung der Anbieter getrennt bleiben.
 */
export interface Business {
  id: string;
  name: string;
  color: string | null;
  isDefault: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Projekt: oberste Ebene im Geschäft. Ein Projekt kann ein Kunde sein oder
 * ein internes Vorhaben; eine eigene Kundenebene gibt es nicht.
 */
export interface Project {
  id: string;
  businessId: string;
  name: string;
  color: string | null;
  /** Projektbild bzw. Logo als Bild-Medium */
  imageMediaId: string | null;
  /** Automatisch angelegtes Projekt „Intern" des Geschäfts */
  isInternal: boolean;
  /** Ansicht der Aufgaben: als Liste (Chats) oder als Board (Kanban) */
  view: ProjectView;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Category {
  id: string;
  name: string;
  color: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: string;
  projectId: string;
  title: string;
  status: TaskStatus;
  /** Laufende Nummer des aktuellen Schritts, beginnt bei 1. */
  step: number;
  priority: Priority;
  dueAt: string | null;
  assigneeIds: string[];
  categoryIds: string[];
  /** Board und Spalte, auf der die Aufgabe liegt (beides leer: noch keinem Board zugeordnet) */
  boardId: string | null;
  columnId: string | null;
  /** Platz in der Spalte; kleinere Werte stehen oben */
  boardOrder: number;
  createdBy: string;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  /** Gesetzt, sobald die Aufgabe erledigt ist (→ Archiv). */
  archivedAt: string | null;
  /** Hauptaufgabe, wenn dies eine Unteraufgabe ist (nur eine Ebene, gleiches Projekt) */
  parentId: string | null;
  /** Reihenfolge unter der Hauptaufgabe, unabhängig vom Board. */
  subtaskOrder?: number;
  createdAt: string;
  updatedAt: string;
}

/** Inhaltspunkt aus einem Video / einer Sprachnachricht. */
export interface ContentPoint {
  /** Sekunden ab Start. */
  at: number;
  text: string;
}

export interface Media {
  id: string;
  kind: 'bild' | 'video' | 'sprache' | 'datei' | 'link';
  mime: string | null;
  fileName: string | null;
  size: number | null;
  durationSec: number | null;
  width: number | null;
  height: number | null;
  status: MediaStatus;
  /** Bei Video-Links die Original-URL. */
  url: string | null;
  linkTitle: string | null;
  linkSite: string | null;
  /** Beschreibung aus den Metadaten der Seite (og:description) */
  linkDescription: string | null;
  /** Vorschaubild liegt auf dem Server (`/api/media/:id/preview`); Maße in width/height */
  linkImage: boolean;
  summary: string | null;
  points: ContentPoint[];
  /** Zeitmarken der gespeicherten Standbilder eines Videos (Sekunden ab Start) */
  frames: number[];
  transcript: string | null;
  imageText: string | null;
  /** Fehler der KI-Auswertung (Medium selbst ist trotzdem nutzbar). */
  aiError: string | null;
  /** Zeitpunkt der KI-Auswertung; null = steht noch aus. */
  aiAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  id: string;
  taskId: string;
  authorId: string | null;
  authorName: string;
  authorKind: AuthorKind;
  kind: MessageKind;
  body: string;
  stepRole: StepRole | null;
  checkResult: CheckResult | null;
  /** Schritt, zu dem die Nachricht gehört (vom Server festgelegt). */
  stepNo: number;
  replyToId: string | null;
  /** Zitierter Inhaltspunkt (Index in media.points der Antwort-Nachricht). */
  replyPoint: number | null;
  mediaId: string | null;
  isMilestone: boolean;
  questionTo: string | null;
  questionOpen: boolean;
  /** Fragebogen einer Rückfrage (sonst null) */
  form: QuestionForm | null;
  /** Erwähnte Personen: Benutzer-IDs bzw. `guest:<id>` für Externe */
  mentions: string[];
  /** Zeitpunkt der letzten Textänderung; gesetzt heißt „bearbeitet“ */
  editedAt: string | null;
  /** Aus einer anderen Aufgabe weitergeleitet */
  forwarded: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Antwort einer Person auf den Fragebogen einer Rückfrage. */
export interface FormResponse {
  id: string; // `${messageId}:${userId}`
  messageId: string;
  taskId: string;
  userId: string;
  userName: string;
  authorKind: AuthorKind;
  answers: FormAnswers;
  voiceMediaId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Member {
  userId: string;
  name: string;
  email: string;
  role: Role;
  /** Zeitpunkt der letzten Änderung des Profilbilds, null = keins */
  avatarAt: string | null;
}

/** Reaktion einer Person auf eine Nachricht (höchstens eine je Person). */
/** Punkt einer Checkliste in einer Aufgabe (leichter als eine Unteraufgabe, ohne Chat) */
export interface ChecklistItem {
  id: string;
  taskId: string;
  text: string;
  done: boolean;
  doneBy: string | null;
  doneByName: string | null;
  doneAt: string | null;
  /** Reihenfolge; Lücken von 1000, beim Einschieben der Mittelwert */
  order: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Reaction {
  id: string; // `${messageId}:${userId}`
  messageId: string;
  taskId: string;
  userId: string;
  userName: string;
  authorKind: AuthorKind;
  emoji: string;
  createdAt: string;
  updatedAt: string;
}

/** Pro Benutzer: Lesestand, Anpinnen, Stummschalten. */
export interface TaskUserState {
  id: string; // `${taskId}:${userId}`
  taskId: string;
  userId: string;
  pinned: boolean;
  muted: boolean;
  lastReadAt: string | null;
  /** Markierte Nachrichten dieser Aufgabe (nur für diese Person) */
  starred: string[];
  updatedAt: string;
}

/** Spalte einem Board; die Reihenfolge ergibt sich aus der Liste. */
export interface BoardColumn {
  id: string;
  name: string;
  color: string | null;
}

/** Board (Kanban) eines Projekts; ein Projekt kann mehrere haben. */
export interface Board {
  id: string;
  projectId: string;
  name: string;
  columns: BoardColumn[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntityMap {
  business: Business;
  project: Project;
  board: Board;
  category: Category;
  task: Task;
  message: Message;
  media: Media;
  taskUserState: TaskUserState;
  formResponse: FormResponse;
  reaction: Reaction;
  checklistItem: ChecklistItem;
}

export type EntityName = keyof EntityMap;

export const ENTITY_NAMES: EntityName[] = ['business', 'project', 'board', 'category', 'task', 'message', 'media', 'taskUserState', 'formResponse', 'reaction', 'checklistItem'];

/** Anlässe für Benachrichtigungen, einzeln schaltbar. */
export type NotifyEvent = 'nachricht' | 'aufgabe' | 'zuweisung' | 'rueckfrage' | 'antwort' | 'erwaehnung' | 'reaktion';

/** Einstellungen einer Person für Benachrichtigungen (gelten auf allen Geräten). */
export interface NotifyPrefs {
  /** alle: jede Aufgabe des Teams; beteiligt: nur Aufgaben, die ich angelegt habe, die mir zugewiesen sind oder in denen ich geschrieben habe */
  scope: 'alle' | 'beteiligt';
  events: Record<NotifyEvent, boolean>;
}

export const DEFAULT_NOTIFY_PREFS: NotifyPrefs = {
  scope: 'alle',
  events: { nachricht: true, aufgabe: true, zuweisung: true, rueckfrage: true, antwort: true, erwaehnung: true, reaktion: true },
};
